import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, IndianRupee, User, CreditCard, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/formatters';
import { useEmployees } from '@/api/queries/employee.queries';
import {
  useSalaryStructures, useCreateSalaryStructure,
  useUpdateSalaryStructure, useDeleteSalaryStructure, usePreviewSalaryStructure,
} from '@/api/queries/payroll.queries';

const EMPLOYMENT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'DAILY_WAGE'];

// ─── Live Calculator Preview ──────────────────────────────────────────────────

function CalcPreview({ form }) {
  const preview = usePreviewSalaryStructure();
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!form.ctc_annual || Number(form.ctc_annual) < 1) { setResult(null); return; }
    const t = setTimeout(() => {
      preview.mutate({
        ctc_annual: Number(form.ctc_annual),
        basic_percent: Number(form.basic_percent) || 40,
        hra_percent: Number(form.hra_percent) || 50,
        da: Number(form.da) || 0,
        conveyance: Number(form.conveyance) || 1600,
        medical_allowance: Number(form.medical_allowance) || 1250,
        lta: Number(form.lta) || 0,
        pf_applicable: form.pf_applicable,
        pf_on_full_basic: form.pf_on_full_basic,
      }, { onSuccess: d => setResult(d?.data) });
    }, 700);
    return () => clearTimeout(t);
  }, [form.ctc_annual, form.basic_percent, form.hra_percent, form.da, form.conveyance, form.medical_allowance, form.lta, form.pf_applicable, form.pf_on_full_basic]);

  if (!result) return null;
  const c = result.components;
  const calc = result.calculation;

  return (
    <div className="bg-[var(--bg-muted)] rounded-[12px] p-4 space-y-3">
      <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-tertiary)]">Live Preview ({result.working_days} working days/month)</p>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[12px]">
        {[['Basic', c.basic],['HRA', c.hra],['DA', c.da],['Conveyance', c.conveyance],['Medical', c.medicalAllowance],['LTA', c.lta],['Special Allowance', c.specialAllowance]].map(([l,v])=>(
          <div key={l} className="flex justify-between"><span className="text-[var(--text-secondary)]">{l}</span><span className="font-medium">{formatCurrency(v)}</span></div>
        ))}
        <div className="col-span-2 flex justify-between font-bold border-t border-[var(--border)] pt-1">
          <span>Gross Monthly</span><span className="text-[var(--accent)]">{formatCurrency(calc.grossSalary)}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[12px] border-t border-[var(--border)] pt-2">
        {[['PF (Employee)', calc.pfEmployee],['ESIC (Employee)', calc.esicEmployee],['Prof. Tax', calc.pt],['TDS', calc.tds]].map(([l,v])=>(
          <div key={l} className="flex justify-between"><span className="text-[var(--text-secondary)]">{l}</span><span className="text-red-500">−{formatCurrency(v)}</span></div>
        ))}
        <div className="col-span-2 flex justify-between font-bold border-t border-[var(--border)] pt-1">
          <span>Net Take-Home</span><span className="text-green-600">{formatCurrency(calc.netPay)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const BLANK = {
  employee_id:'', employee_name:'', employee_code:'', department:'', designation:'',
  employment_type:'FULL_TIME', effective_from: new Date().toISOString().slice(0,10),
  ctc_annual:'', basic_percent:40, hra_percent:50, da:0, conveyance:1600, medical_allowance:1250, lta:0,
  pf_applicable:true, pf_on_full_basic:false,
  bank_name:'', account_number:'', ifsc_code:'', account_holder_name:'',
  uan:'', esic_ip_number:'', pan:'',
};

function Modal({ existing, employees, onClose }) {
  const create = useCreateSalaryStructure();
  const update = useUpdateSalaryStructure();
  const [tab, setTab] = useState('salary');
  const [form, setForm] = useState(existing ? { ...BLANK, ...existing } : BLANK);
  const [showAcc, setShowAcc] = useState(false);

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleEmpSelect = (emp) => {
    const id = emp.id || emp.employee_id;
    setForm(p => ({
      ...p,
      employee_id: id,
      employee_name: `${emp.first_name} ${emp.last_name}`,
      employee_code: emp.employee_code || '',
      department: typeof emp.department === 'string' ? emp.department : emp.department?.name || '',
      designation: typeof emp.designation === 'string' ? emp.designation : emp.designation?.name || '',
      employment_type: emp.employment_type || 'FULL_TIME',
    }));
  };

  const handleSave = async () => {
    if (!form.employee_id) return toast.error('Select an employee');
    if (!form.ctc_annual || Number(form.ctc_annual) < 1) return toast.error('Enter a valid CTC');
    try {
      const payload = { ...form, ctc_annual: Number(form.ctc_annual), basic_percent: Number(form.basic_percent), hra_percent: Number(form.hra_percent), da: Number(form.da), conveyance: Number(form.conveyance), medical_allowance: Number(form.medical_allowance), lta: Number(form.lta) };
      existing ? await update.mutateAsync({ id: existing.id, ...payload }) : await create.mutateAsync(payload);
      toast.success(existing ? 'Updated' : 'Created');
      onClose();
    } catch(e) { toast.error(e?.response?.data?.error?.message || 'Failed to save'); }
  };

  const busy = create.isPending || update.isPending;
  const inp = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  const lbl = "text-[12px] font-semibold text-[var(--text-secondary)]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-[18px] shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)] shrink-0">
          <div>
            <p className="text-[15px] font-bold text-[var(--text-primary)]">{existing ? 'Edit Salary Structure' : 'Add Salary Structure'}</p>
            {form.employee_name && <p className="text-[12px] text-[var(--text-tertiary)]">{form.employee_name} · {form.department}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-muted)]"><X size={16}/></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 shrink-0">
          {['salary','bank','statutory'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-full text-[12px] font-semibold capitalize transition-colors ${tab===t ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
              {t === 'bank' ? 'Bank Details' : t === 'statutory' ? 'Statutory IDs' : 'Salary'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {tab === 'salary' && (<>
            {!existing && (
              <div className="space-y-1.5">
                <label className={lbl}>Employee *</label>
                <select className={inp} value={form.employee_id}
                  onChange={e => { const emp = employees.find(x => (x.id||x.employee_id) === e.target.value); if(emp) handleEmpSelect(emp); }}>
                  <option value="">— Select employee —</option>
                  {employees.map(e => { const id=e.id||e.employee_id; return <option key={id} value={id}>{e.first_name} {e.last_name} ({e.employee_code})</option>; })}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={lbl}>Employment Type</label>
                <select className={inp} value={form.employment_type} onChange={e => F('employment_type', e.target.value)}>
                  {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className={lbl}>Effective From *</label>
                <input type="date" className={inp} value={form.effective_from} onChange={e => F('effective_from', e.target.value)}/>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={lbl}>Annual CTC (₹) *</label>
              <div className="relative">
                <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"/>
                <input type="number" className={`${inp} pl-8`} placeholder="e.g. 600000" value={form.ctc_annual} onChange={e => F('ctc_annual', e.target.value)}/>
              </div>
              {Number(form.ctc_annual) > 0 && <p className="text-[11px] text-[var(--text-tertiary)]">Monthly CTC: {formatCurrency(Number(form.ctc_annual)/12)}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><label className={lbl}>Basic (% of Monthly CTC)</label><input type="number" className={inp} min={10} max={70} value={form.basic_percent} onChange={e => F('basic_percent', e.target.value)}/></div>
              <div className="space-y-1.5"><label className={lbl}>HRA (% of Basic)</label><input type="number" className={inp} min={10} max={60} value={form.hra_percent} onChange={e => F('hra_percent', e.target.value)}/></div>
              <div className="space-y-1.5"><label className={lbl}>Conveyance ₹/month</label><input type="number" className={inp} value={form.conveyance} onChange={e => F('conveyance', e.target.value)}/></div>
              <div className="space-y-1.5"><label className={lbl}>Medical Allowance ₹/month</label><input type="number" className={inp} value={form.medical_allowance} onChange={e => F('medical_allowance', e.target.value)}/></div>
              <div className="space-y-1.5"><label className={lbl}>DA ₹/month</label><input type="number" className={inp} value={form.da} onChange={e => F('da', e.target.value)}/></div>
              <div className="space-y-1.5"><label className={lbl}>LTA ₹/month</label><input type="number" className={inp} value={form.lta} onChange={e => F('lta', e.target.value)}/></div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer text-[13px]">
                <input type="checkbox" checked={form.pf_applicable} onChange={e => F('pf_applicable', e.target.checked)} className="w-4 h-4 accent-[var(--accent)]"/>PF Applicable
              </label>
              {form.pf_applicable && (
                <label className="flex items-center gap-2 cursor-pointer text-[13px]">
                  <input type="checkbox" checked={form.pf_on_full_basic} onChange={e => F('pf_on_full_basic', e.target.checked)} className="w-4 h-4 accent-[var(--accent)]"/>PF on full basic (no ₹15k cap)
                </label>
              )}
            </div>
            <CalcPreview form={form}/>
          </>)}

          {tab === 'bank' && (
            <div className="space-y-4">
              <div className="p-3 rounded-[10px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-[12px] text-blue-700 dark:text-blue-400">
                Employee's personal bank account for salary credit. Printed on payslip.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><label className={lbl}>Bank Name</label><input className={inp} placeholder="e.g. ICICI Bank" value={form.bank_name} onChange={e => F('bank_name', e.target.value)}/></div>
                <div className="space-y-1.5"><label className={lbl}>Account Holder Name</label><input className={inp} placeholder="As per bank" value={form.account_holder_name} onChange={e => F('account_holder_name', e.target.value)}/></div>
                <div className="space-y-1.5 col-span-2">
                  <label className={lbl}>Account Number</label>
                  <div className="relative">
                    <input type={showAcc ? 'text' : 'password'} className={`${inp} pr-10`} placeholder="Account number" value={form.account_number} onChange={e => F('account_number', e.target.value)}/>
                    <button type="button" onClick={() => setShowAcc(!showAcc)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]">{showAcc ? <EyeOff size={14}/> : <Eye size={14}/>}</button>
                  </div>
                </div>
                <div className="space-y-1.5"><label className={lbl}>IFSC Code</label><input className={inp} placeholder="e.g. ICIC0001234" value={form.ifsc_code} onChange={e => F('ifsc_code', e.target.value.toUpperCase())}/></div>
              </div>
            </div>
          )}

          {tab === 'statutory' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><label className={lbl}>UAN (PF Universal Account)</label><input className={inp} placeholder="12-digit UAN" value={form.uan} onChange={e => F('uan', e.target.value)}/><p className="text-[11px] text-[var(--text-tertiary)]">Required for Form 16 & ECR</p></div>
                <div className="space-y-1.5"><label className={lbl}>ESIC IP Number</label><input className={inp} placeholder="Employee IP no." value={form.esic_ip_number} onChange={e => F('esic_ip_number', e.target.value)}/></div>
                <div className="space-y-1.5 col-span-2"><label className={lbl}>PAN Number</label><input className={inp} placeholder="e.g. ABCDE1234F" maxLength={10} value={form.pan} onChange={e => F('pan', e.target.value.toUpperCase())}/><p className="text-[11px] text-[var(--text-tertiary)]">Required for TDS & Form 16</p></div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--border)] shrink-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={busy}>{existing ? 'Update' : 'Create'} Structure</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalaryStructuresPage() {
  const { data: ssResp, isLoading } = useSalaryStructures({ limit: 100 });
  const { data: empResp } = useEmployees({ status: 'ACTIVE', limit: 200 });
  const deleteSS = useDeleteSalaryStructure();
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');

  const structures = ssResp?.data || [];
  const employees = (empResp?.data || []).filter(e => e.status === 'ACTIVE' || !e.status);
  const assignedIds = new Set(structures.map(s => s.employee_id));
  const unassigned = employees.filter(e => !assignedIds.has(e.id || e.employee_id));

  const filtered = structures.filter(s =>
    !search || [s.employee_name, s.employee_code, s.department].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleDelete = async (ss) => {
    if (!confirm(`Remove salary structure for ${ss.employee_name}?`)) return;
    try { await deleteSS.mutateAsync(ss.id); toast.success('Removed'); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Salary Structures"
        subtitle={`${structures.length} configured · ${unassigned.length} employees pending`}
        breadcrumb={[{ label: 'Payroll', href: '/payroll' }, { label: 'Salary Structures' }]}
        actions={<Button icon={Plus} onClick={() => setModal({ existing: null })}>Add Structure</Button>}
      />

      {unassigned.length > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-[12px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <User size={15} className="text-amber-600 shrink-0"/>
          <p className="text-[12px] text-amber-700 dark:text-amber-400 flex-1">
            <b>{unassigned.length} employee{unassigned.length > 1 ? 's' : ''}</b> have no salary structure: {unassigned.slice(0,4).map(e=>`${e.first_name} ${e.last_name}`).join(', ')}{unassigned.length > 4 ? ` +${unassigned.length-4} more` : ''}
          </p>
          <Button size="sm" variant="outline" onClick={() => setModal({ existing: null })}>Set Up Now</Button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <input className="max-w-sm h-9 px-3 flex-1 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          placeholder="Search name, code, department..." value={search} onChange={e => setSearch(e.target.value)}/>
        <span className="text-[12px] text-[var(--text-tertiary)]">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)]">
              {['Employee','Annual CTC','Gross/Month','Net/Month','PF','Bank','Actions'].map(h=>(
                <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {isLoading && <tr><td colSpan={7} className="px-5 py-10 text-center text-[13px] text-[var(--text-tertiary)]">Loading...</td></tr>}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-14 text-center">
                <IndianRupee size={32} className="mx-auto text-[var(--text-tertiary)] mb-3"/>
                <p className="text-[14px] font-semibold text-[var(--text-secondary)]">No salary structures configured</p>
                <p className="text-[12px] text-[var(--text-tertiary)] mt-1">Add salary for each employee to enable payroll processing</p>
              </td></tr>
            )}
            {filtered.map(ss => {
              const estNet = Number(ss.gross_monthly) - Math.min(Number(ss.basic), 15000) * 0.12 - 200;
              return (
                <tr key={ss.id} className="hover:bg-[var(--bg-muted)] transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={ss.employee_name} size="sm"/>
                      <div>
                        <p className="text-[13px] font-semibold text-[var(--text-primary)]">{ss.employee_name}</p>
                        <p className="text-[11px] text-[var(--text-tertiary)]">{ss.employee_code} · {ss.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[13px] font-semibold">{formatCurrency(ss.ctc_annual)}</td>
                  <td className="px-5 py-3.5 text-[13px] font-semibold text-[var(--accent)]">{formatCurrency(ss.gross_monthly)}</td>
                  <td className="px-5 py-3.5 text-[13px] font-bold text-green-600">~{formatCurrency(Math.max(0, estNet))}</td>
                  <td className="px-5 py-3.5">
                    {ss.pf_applicable
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold">Yes</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--bg-muted)] text-[var(--text-tertiary)]">No</span>}
                  </td>
                  <td className="px-5 py-3.5 text-[12px]">
                    {ss.bank_name
                      ? <span className="flex items-center gap-1 text-green-600"><CreditCard size={11}/>{ss.bank_name}</span>
                      : <span className="text-amber-500 text-[11px]">Not set</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-1">
                      <button onClick={() => setModal({ existing: ss })} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-[var(--accent)]"><Pencil size={13}/></button>
                      <button onClick={() => handleDelete(ss)} className="p-1.5 rounded-[6px] hover:bg-red-50 dark:hover:bg-red-900/20 text-[var(--text-tertiary)] hover:text-red-600"><Trash2 size={13}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal !== null && <Modal existing={modal.existing} employees={employees} onClose={() => setModal(null)}/>}
    </div>
  );
}
